/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CHAT_LINK_HREF } from '@/lib/link/link-picker';
import { useSentinelLink } from '@/lib/link/use-sentinel-link';
import { openSierraChat } from './sierra-chat.utils';

/**
 * Hrefs that open the chatbot instead of navigating (UCR-2926). `CHAT_LINK_HREF` is what the link
 * picker's `type: 'chat'` resolves to; the `#openlink-openchat` alias keeps parity with the SFRA
 * `openLinks.js` sentinel so hand-authored links work too.
 */
const CHAT_HREFS = [CHAT_LINK_HREF, '#openlink-openchat'];

/**
 * Global chat deep-link interceptor (UCR-2926). Mounts no UI; {@link useSentinelLink} installs the
 * single capture-phase click listener that catches clicks on any anchor whose href is a chat
 * sentinel — anywhere on the page, in any Page Designer component. Fail-soft via
 * {@link openSierraChat} when the embed isn't ready.
 *
 * Mounted once in root.tsx beside `<SierraChat>`, but deliberately **ungated** (UCR-3021): with the
 * chat disabled an authored "Open chat" CTA still renders its `#open-chat` sentinel href, and with no
 * listener the browser follows that dead anchor and pushes a junk hash onto the history stack. Mounted
 * always, the click is swallowed here instead.
 */
export default function ChatLinkInterceptor(): null {
    useSentinelLink(CHAT_HREFS, () => {
        void openSierraChat();
    });

    return null;
}
